import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useChatPopup } from '../../contexts/ChatPopupContext';
import ChatPopup from './ChatPopup';

const ChatPopupManager = () => {
  const { popups, closeChatPopup, toggleMinimizePopup, updatePopupRoom } = useChatPopup();
  const location = useLocation();
  const [sidebarWidth, setSidebarWidth] = useState(0); // Width of sidebar when open
  const [sidebarMargin, setSidebarMargin] = useState(20); // Margin of sidebar from right
  
  // Don't show popups on the chat page
  const isChatPage = location.pathname === '/chat';
  
  // Listen for sidebar state changes
  useEffect(() => {
    const handleSidebarStateChange = (event) => {
      setSidebarWidth(event.detail.sidebarWidth || 0);
      setSidebarMargin(event.detail.sidebarMargin || 20);
    };

    window.addEventListener('chat-sidebar-state-changed', handleSidebarStateChange);
    
    // Check initial state - always account for sidebar width (320px) to prevent overlap
    const sidebar = document.querySelector('.chat-floating-sidebar');
    if (sidebar) {
      setSidebarWidth(320); // Always account for sidebar width (even when collapsed)
      setSidebarMargin(20); // Default sidebar margin
    }

    return () => {
      window.removeEventListener('chat-sidebar-state-changed', handleSidebarStateChange);
    };
  }, []);
  
  if (isChatPage || !popups || Object.keys(popups).length === 0) {
    return null;
  }

  // Calculate positions for multiple popups (stack them)
  const popupEntries = Object.entries(popups);
  const popupCount = popupEntries.length;
  const fullPopupWidth = 380; // Width of full popup
  const minimizedPopupWidth = 300; // Width of minimized popup
  const minimizedPopupHeight = 60; // Height of minimized popup
  const gap = 20; // Gap between popups
  const sidebarGap = sidebarWidth > 0 ? 20 : 0; // Extra gap when sidebar is open
  const baseRightOffset = 20; // Base offset from right edge

  // Separate minimized and full popups
  const minimizedPopups = popupEntries.filter(([_, popupData]) => popupData.isMinimized);
  const fullPopups = popupEntries.filter(([_, popupData]) => !popupData.isMinimized);

  return (
    <>
      {/* Render full popups first (stack horizontally) */}
      {fullPopups.map(([userId, popupData], index) => {
        // Stack full popups horizontally from right to left
        // When sidebar is open, add extra offset to avoid overlap
        const rightOffset = baseRightOffset + sidebarWidth + sidebarGap + (index * (fullPopupWidth + gap));
        
        return (
          <div
            key={userId}
            style={{
              position: 'fixed',
              bottom: '20px',
              right: `${rightOffset}px`,
              zIndex: 10000 + index, // Higher index = higher z-index (on top)
              transition: 'right 0.3s ease', // Smooth transition when sidebar opens/closes
            }}
          >
            <ChatPopup
              userId={popupData.userId}
              userName={popupData.userName}
              userProfilePhoto={popupData.userProfilePhoto}
              initialRoom={popupData.initialRoom}
              initialMessage={popupData.initialMessage || ''}
              isMinimized={popupData.isMinimized}
              onClose={() => closeChatPopup(userId)}
              onMinimize={() => toggleMinimizePopup(userId)}
              onChatInitiated={(room) => updatePopupRoom(userId, room)}
            />
          </div>
        );
      })}

      {/* Render minimized popups (stack vertically, avoid sidebar) */}
      {minimizedPopups.map(([userId, popupData], index) => {
        // Stack minimized popups vertically from bottom, aligned with collapsed sidebar (bottom: 0)
        // Position them to avoid sidebar overlap (always account for sidebar width)
        const bottomOffset = index * (minimizedPopupHeight + 10); // Stack vertically with 10px gap, starting from bottom: 0
        // Calculate right offset to avoid sidebar (always account for sidebar width to prevent overlap)
        const rightOffset = baseRightOffset + sidebarWidth + sidebarGap;
        
        return (
          <div
            key={userId}
            style={{
              position: 'fixed',
              bottom: `${bottomOffset}px`,
              right: `${rightOffset}px`,
              zIndex: 10000 + fullPopups.length + index, // Higher z-index than full popups and sidebar
              transition: 'right 0.3s ease, bottom 0.3s ease', // Smooth transition
            }}
          >
            <ChatPopup
              userId={popupData.userId}
              userName={popupData.userName}
              userProfilePhoto={popupData.userProfilePhoto}
              initialRoom={popupData.initialRoom}
              initialMessage={popupData.initialMessage || ''}
              isMinimized={popupData.isMinimized}
              onClose={() => closeChatPopup(userId)}
              onMinimize={() => toggleMinimizePopup(userId)}
              onChatInitiated={(room) => updatePopupRoom(userId, room)}
            />
          </div>
        );
      })}
    </>
  );
};

export default ChatPopupManager;

